import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import PaymentTermsSelector from '@/components/PaymentTermsSelector';
import { 
  ArrowLeft, Home, Plus, Search, Building2, Phone, Mail, MapPin, Calendar, 
  PoundSterling, Clock, CheckCircle, AlertCircle, Pause, XCircle, ChevronRight,
  Users, FileText, Camera, MessageSquare, AlertTriangle, TrendingUp, X, CalendarDays
} from 'lucide-react';

import API_URL from '@/config/api';
import { formatApiError } from '@/utils/apiError';
import logger from '@/utils/logger';

// Status configurations
const PROJECT_STATUSES = {
  upcoming: { label: 'Upcoming', color: '#3B82F6', bgColor: 'bg-blue-100', textColor: 'text-blue-800', icon: Calendar },
  in_progress: { label: 'In Progress', color: '#F59E0B', bgColor: 'bg-amber-100', textColor: 'text-amber-800', icon: Clock },
  completed: { label: 'Completed', color: '#22C55E', bgColor: 'bg-green-100', textColor: 'text-green-800', icon: CheckCircle },
  on_hold: { label: 'On Hold', color: '#6B7280', bgColor: 'bg-gray-100', textColor: 'text-gray-800', icon: Pause },
  cancelled: { label: 'Cancelled', color: '#EF4444', bgColor: 'bg-red-100', textColor: 'text-red-800', icon: XCircle }
};

const Projects = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isStaff } = useAuth();
  
  // Detect section from URL path
  const pathSection = location.pathname.split('/')[1];
  const sectionFilter = ['probound', 'proepoxy', 'prodecor'].includes(pathSection) ? pathSection : null;

  // Section-aware branding
  const SECTION_BRAND = {
    probound: {
      label: 'ProBound',
      borderColor: '#2F5E96',
      logo: 'https://customer-assets.emergentagent.com/job_resin-quote-calc/artifacts/0yxgnasw_IMG_8077.jpeg',
    },
    proepoxy: {
      label: 'ProEpoxy',
      borderColor: '#7A0B7A',
      logo: 'https://customer-assets.emergentagent.com/job_76625416-d43e-411a-b4d0-3b73c58f517c/artifacts/ta6bz3y6_IMG_8292.jpeg',
    },
    prodecor: {
      label: 'ProDecor',
      borderColor: '#D5AB09',
      logo: 'https://customer-assets.emergentagent.com/job_d9177fe4-220c-4669-9998-86c6e454a508/artifacts/4z707ot7_IMG_8289.jpeg',
    },
  };
  const brand = SECTION_BRAND[sectionFilter] || SECTION_BRAND.probound;
  
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState(() => {
    // When deep-linked from the Staff Dashboard tiles (e.g. /staff/projects?status=in_progress)
    // honour the requested status. Falls back to 'all' for the unfiltered view.
    const sp = new URLSearchParams(location.search);
    return sp.get('status') || 'all';
  });
  const [serviceFilter, setServiceFilter] = useState(sectionFilter || 'all');
  const [mineOnly, setMineOnly] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    customer_address: '',
    service_type: sectionFilter || 'probound',
    project_value: '',
    install_date: '',
    notes: ''
  });
  const [createPaymentTerms, setCreatePaymentTerms] = useState(null);
  const [createPaymentTermsValid, setCreatePaymentTermsValid] = useState(true);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await fetch(`${API_URL}/api/projects`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setProjects(data);
      }
    } catch (error) {
      logger.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = async () => {
    if (!createForm.customer_name) {
      alert('Customer name is required');
      return;
    }
    const value = parseFloat(createForm.project_value) || 0;
    if (value <= 0) {
      alert('Enter the agreed project value (inc VAT)');
      return;
    }
    if (!createPaymentTermsValid) {
      alert('Payment terms must total 100% and every stage needs a label');
      return;
    }

    setCreating(true);
    try {
      const response = await fetch(`${API_URL}/api/projects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...createForm,
          project_value: value,
          payment_terms: createPaymentTerms,
        })
      });

      if (response.ok) {
        const project = await response.json();
        setShowCreateModal(false);
        setCreateForm({
          customer_name: '',
          customer_email: '',
          customer_phone: '',
          customer_address: '',
          service_type: 'probound',
          project_value: '',
          install_date: '',
          notes: ''
        });
        setCreatePaymentTerms(null);
        navigate(`/projects/${project.id}`);
      } else {
        const error = await response.json();
        alert(formatApiError(error) || 'Failed to create project');
      }
    } catch (error) {
      logger.error('Error creating project:', error);
      alert('Failed to create project');
    } finally {
      setCreating(false);
    }
  };

  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         project.reference?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         project.customer_address?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || project.status === statusFilter;
    const matchesSection = serviceFilter === 'all' || project.service_type === serviceFilter;
    const matchesOwner = !mineOnly || project.is_mine;
    return matchesSearch && matchesStatus && matchesSection && matchesOwner;
  });

  // Stats
  const stats = {
    total: projects.length,
    upcoming: projects.filter(p => p.status === 'upcoming').length,
    in_progress: projects.filter(p => p.status === 'in_progress').length,
    completed: projects.filter(p => p.status === 'completed').length,
    total_value: projects.reduce((sum, p) => sum + (p.project_value || 0), 0),
    total_paid: projects.reduce((sum, p) => sum + (p.total_paid || 0), 0),
    total_outstanding: projects.reduce((sum, p) => sum + (p.outstanding || 0), 0)
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount || 0);
  };

  const getStatusBadge = (status) => {
    const config = PROJECT_STATUSES[status] || PROJECT_STATUSES.upcoming;
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.bgColor} ${config.textColor}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-[#E8ECEF]">
      {/* Header */}
      <header className="bg-white shadow-sm border-b-4 border-[#006400]">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Button
            onClick={() => navigate(sectionFilter ? `/${sectionFilter}` : '/')}
            variant="outline"
            className="border-2 border-[#006400] text-[#006400] hover:bg-[#006400]/10 font-medium"
          >
            <Home className="w-4 h-4 mr-2" />
            {sectionFilter ? 'Back' : 'Back to Portal'}
          </Button>
          <img 
            src="https://customer-assets.emergentagent.com/job_b4127cdf-1323-436d-9b7a-27a06c6d6f4a/artifacts/bwvyspsq_Logo_-_Ox.png" 
            alt="Ox Resin" 
            className="h-12 md:h-14 cursor-pointer object-contain"
            onClick={() => navigate(sectionFilter ? `/${sectionFilter}` : '/')}
          />
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6 md:py-8">
        {/* Page Title */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-[#27292E]">Project Management</h1>
            <p className="text-sm text-gray-500 mt-1">Track your installations from start to finish</p>
          </div>
          <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => navigate(sectionFilter ? `/${sectionFilter}/calendar` : '/calendar')}
            className="border-2 border-[#006400] text-[#006400] hover:bg-[#006400]/10"
            data-testid="open-calendar-btn"
          >
            <CalendarDays className="w-4 h-4 mr-2" />
            Calendar
          </Button>
          <Button
            onClick={() => setShowCreateModal(true)}
            className="bg-[#006400] hover:bg-[#004d00] text-white"
            data-testid="create-project-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Project
          </Button>
          </div>
        </div>

        {/* Section Filter Chips */}
        <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="section-filter-chips">
          <span className="text-xs uppercase tracking-wide font-semibold text-gray-500 mr-1">Section:</span>
          {[
            { id: 'all', label: 'All', color: '#27292E' },
            { id: 'probound', label: 'ProBound', color: '#2F5E96' },
            { id: 'proepoxy', label: 'ProEpoxy', color: '#7A0B7A' },
            { id: 'prodecor', label: 'ProDecor', color: '#D5AB09' },
          ].map(s => {
            const isActive = serviceFilter === s.id;
            return (
              <button
                key={s.id}
                onClick={() => setServiceFilter(s.id)}
                data-testid={`section-filter-${s.id}`}
                className={`px-3 py-1.5 rounded-full text-sm font-medium border-2 transition-all ${
                  isActive ? 'text-white shadow-sm' : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
                style={isActive ? { backgroundColor: s.color, borderColor: s.color } : { borderColor: '#e5e7eb' }}
              >
                {s.label}
              </button>
            );
          })}
        </div>

        {/* Financial Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Building2 className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Total Projects</p>
                <p className="text-xl font-bold text-[#27292E]">{stats.total}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <PoundSterling className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Total Value</p>
                <p className="text-xl font-bold text-green-600">{formatCurrency(stats.total_value)}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <TrendingUp className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Payments Received</p>
                <p className="text-xl font-bold text-emerald-600">{formatCurrency(stats.total_paid)}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <AlertCircle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Outstanding</p>
                <p className="text-xl font-bold text-amber-600">{formatCurrency(stats.total_outstanding)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Status Filters */}
        <div className="flex flex-wrap gap-2 mb-6 items-center">
          {isStaff && (
            <button
              onClick={() => setMineOnly((v) => !v)}
              data-testid="projects-mine-only-toggle"
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all border ${mineOnly ? 'bg-[#006400] text-white border-[#006400]' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            >
              {mineOnly ? 'Showing my projects only' : 'Mine only'} ({projects.filter((p) => p.is_mine).length})
            </button>
          )}
          <button
            onClick={() => setStatusFilter('all')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              statusFilter === 'all' 
                ? 'bg-[#27292E] text-white' 
                : 'bg-white text-gray-600 border hover:bg-gray-50'
            }`}
          >
            All ({stats.total})
          </button>
          {Object.entries(PROJECT_STATUSES).map(([key, config]) => (
            <button
              key={key}
              onClick={() => setStatusFilter(statusFilter === key ? 'all' : key)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                statusFilter === key 
                  ? 'text-white' 
                  : 'bg-white border hover:opacity-80'
              }`}
              style={{ 
                backgroundColor: statusFilter === key ? config.color : undefined,
                color: statusFilter === key ? 'white' : config.color,
                borderColor: config.color
              }}
            >
              {config.label} ({projects.filter(p => p.status === key).length})
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by customer name, reference, or address..."
              className="pl-10 h-12 border-2"
            />
          </div>
        </div>

        {/* Projects List */}
        {loading ? (
          <div className="bg-white rounded-2xl p-12 text-center text-gray-500">
            Loading projects...
          </div>
        ) : filteredProjects.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center">
            <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 mb-2">No projects found</p>
            <p className="text-sm text-gray-400">
              Projects are created when you mark a lead as &quot;Won&quot; and click &quot;Convert to Project&quot;
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredProjects.map((project) => (
              <div 
                key={project.id}
                onClick={() => navigate(`/projects/${project.id}`)}
                className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 hover:shadow-lg hover:border-[#006400]/30 transition-all cursor-pointer"
                data-testid={`project-${project.id}`}
              >
                <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                  {/* Left - Main Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <span className="text-xs font-mono text-gray-400">{project.reference}</span>
                      {getStatusBadge(project.status)}
                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                        {project.service_type?.toUpperCase()}
                      </span>
                      {isStaff && project.owner_name && (
                        <span className={`text-xs px-2 py-0.5 rounded-full border ${project.is_mine ? 'bg-green-50 text-green-800 border-green-200' : 'bg-blue-50 text-blue-800 border-blue-200'}`} data-testid={`project-owner-${project.id}`}>
                          {project.is_mine ? 'Created by you' : `Installer: ${project.owner_name}`}
                        </span>
                      )}
                    </div>
                    <h3 className="font-bold text-lg text-[#27292E] mb-1">{project.customer_name}</h3>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-500">
                      {project.customer_phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="w-3.5 h-3.5 text-gray-400" />
                          {project.customer_phone}
                        </span>
                      )}
                      {project.customer_address && (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3.5 h-3.5 text-gray-400" />
                          {project.customer_address}
                        </span>
                      )}
                      {project.install_date && (
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5 text-gray-400" />
                          {project.install_date}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Middle - Progress */}
                  <div className="lg:w-48">
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-gray-500">Progress</span>
                      <span className="font-medium">{project.progress_percent || 0}%</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-[#006400] to-[#16a34a] rounded-full transition-all"
                        style={{ width: `${project.progress_percent || 0}%` }}
                      />
                    </div>
                  </div>

                  {/* Right - Financials */}
                  <div className="lg:w-48 lg:text-right">
                    <div className="text-lg font-bold text-[#27292E]">
                      {formatCurrency(project.project_value)}
                    </div>
                    <div className="flex items-center gap-2 lg:justify-end text-sm">
                      <span className="text-green-600">{formatCurrency(project.total_paid)} paid</span>
                      {project.outstanding > 0 && (
                        <span className="text-amber-600">({formatCurrency(project.outstanding)} due)</span>
                      )}
                    </div>
                  </div>

                  {/* Arrow */}
                  <ChevronRight className="w-5 h-5 text-gray-300 hidden lg:block" />
                </div>

                {/* Quick Stats Row */}
                <div className="flex flex-wrap gap-3 mt-4 pt-4 border-t border-gray-100">
                  {project.team?.length > 0 && (
                    <span className="inline-flex items-center gap-1 text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded">
                      <Users className="w-3 h-3" />
                      {project.team.length} team
                    </span>
                  )}
                  {project.photos?.length > 0 && (
                    <span className="inline-flex items-center gap-1 text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded">
                      <Camera className="w-3 h-3" />
                      {project.photos.length} photos
                    </span>
                  )}
                  {project.communications?.length > 0 && (
                    <span className="inline-flex items-center gap-1 text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded">
                      <MessageSquare className="w-3 h-3" />
                      {project.communications.length} comms
                    </span>
                  )}
                  {project.issues?.filter(i => !i.resolved).length > 0 && (
                    <span className="inline-flex items-center gap-1 text-xs text-red-600 bg-red-50 px-2 py-1 rounded">
                      <AlertTriangle className="w-3 h-3" />
                      {project.issues.filter(i => !i.resolved).length} issues
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Create Project Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-[#27292E]">Create New Project</h3>
              <button onClick={() => setShowCreateModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <Label>Customer Name *</Label>
                <Input
                  value={createForm.customer_name}
                  onChange={(e) => setCreateForm({ ...createForm, customer_name: e.target.value })}
                  placeholder="e.g. John Smith"
                  className="mt-1"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={createForm.customer_phone}
                    onChange={(e) => setCreateForm({ ...createForm, customer_phone: e.target.value })}
                    placeholder="07xxx xxxxxx"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={createForm.customer_email}
                    onChange={(e) => setCreateForm({ ...createForm, customer_email: e.target.value })}
                    placeholder="email@example.com"
                    className="mt-1"
                  />
                </div>
              </div>
              <div>
                <Label>Address</Label>
                <Input
                  value={createForm.customer_address}
                  onChange={(e) => setCreateForm({ ...createForm, customer_address: e.target.value })}
                  placeholder="Full address"
                  className="mt-1"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Service Type</Label>
                  <select
                    value={createForm.service_type}
                    onChange={(e) => setCreateForm({ ...createForm, service_type: e.target.value })}
                    className="mt-1 w-full border-2 rounded-lg p-2.5"
                  >
                    <option value="probound">ProBound</option>
                    <option value="proepoxy">ProEpoxy</option>
                    <option value="prodecor">ProDecor</option>
                  </select>
                </div>
                <div>
                  <Label>Project Value inc. VAT (£) <span className="text-rose-500">*</span></Label>
                  <Input
                    type="number"
                    value={createForm.project_value}
                    onChange={(e) => setCreateForm({ ...createForm, project_value: e.target.value })}
                    placeholder="0.00"
                    className="mt-1"
                    data-testid="new-project-value"
                  />
                </div>
              </div>
              <div>
                <PaymentTermsSelector
                  grandTotal={parseFloat(createForm.project_value) || 0}
                  value={createPaymentTerms}
                  onChange={setCreatePaymentTerms}
                  onValidityChange={setCreatePaymentTermsValid}
                  serviceType={createForm.service_type}
                />
              </div>
              <div>
                <Label>Install Date</Label>
                <Input
                  type="date"
                  value={createForm.install_date}
                  onChange={(e) => setCreateForm({ ...createForm, install_date: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Notes</Label>
                <textarea
                  value={createForm.notes}
                  onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })}
                  className="mt-1 w-full border-2 rounded-lg p-3 min-h-[80px]"
                  placeholder="Any additional notes about the project..."
                />
              </div>
              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowCreateModal(false)} className="flex-1">
                  Cancel
                </Button>
                <Button 
                  onClick={handleCreateProject} 
                  disabled={!createForm.customer_name || !parseFloat(createForm.project_value) || !createPaymentTermsValid || creating}
                  className="flex-1 bg-[#006400] hover:bg-[#004d00]"
                >
                  {creating ? 'Creating...' : 'Create Project'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Projects;
